import Button from "@/components/atoms/Button";
import { DatePicker } from "@/components/atoms/DatePicker";
import { useForm, UseFormReturn, Controller } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Input from "@/components/atoms/Input";
import AutoInvoiceSchema from "@/schemas/autoInvoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import AutoInvoice from "@/types/AutoInvoice";
import Select from "@/components/molecules/Select";
import { Account } from "@/types/Account";
import Link from "@/components/atoms/Link";
import ErrorType from "@/types/ErrorType";
import { capitalize } from "@/lib/utils";
import type { NestedItem } from "@/types/NestedItem";
import axios from 'axios';
import { FieldValues } from 'react-hook-form';
import { useState } from 'react';
import Spinner from '@/components/atoms/Spinner';
import ManualInvoiceTable from "./ManualInvoiceTable";
import ForwardedNumericFormat from "@/components/atoms/ForwardedNumericFormat";
import { toUtcDate } from "@/lib/utils";
import * as Constants from "@/constants";

export type ProformaProps = {
  accounts: Account[];
  entities: NestedItem[];
  nmq_entities: NestedItem[];
  invoice_type: string;
};

type Props = {
  mode?: "create" | "edit";
  autoInvoice: AutoInvoice | null;
  edit: boolean;
  onSubmit: (data: AutoInvoice) => void;
  data: ProformaProps;
  loading: boolean;
  form: UseFormReturn<any>;
  errors?: ErrorType;
};

interface HandleFieldChangeParams {
  value: string;
  field: FieldValues;
  data: ProformaProps;
  setData: React.Dispatch<React.SetStateAction<ProformaProps>>;
}

export const useProformaForm = (autoInvoice: AutoInvoice | null) =>
  
  useForm({
    resolver: zodResolver(AutoInvoiceSchema),
    defaultValues: autoInvoice
      ? {
          ...autoInvoice,
          account_id: autoInvoice.account.id.toString(),
          entity_id: autoInvoice.entity?.id?.toString() || "",
          nmq_entity_id: autoInvoice.nmq_entity?.id?.toString() || "",
          sow_ids: [],
        }
      : {
          date: new Date(),
          account_id: "",
          entity_id: "",
          nmq_entity_id: "",
          service_period_start: "",
          service_period_end: "",
          sow_ids: [],
          reference_no: "",
          order_ref_no: "",
          payment_terms: "",
          invoicing_queries: "",
          client_contact: "",
          client_address: "",
          client_vat: "",
          exact_customer_id: "",
          client_country: "",
          currency_type: "",
          service_name: "",
          notes: "",
          invoice_type: "PROFORMA INVOICE",
          calculated_data_html: "",
          additional_type: "ADDITIONAL CHARGE",
          additional_amount_description: "",
          additional_cost_discount: 0,
          total_amount: 0,
          total_sub_amount: 0,
          vat_amount: 0,
          vat_percent: 0,
          actual_total_amount: 0,
          unchecked_columns: [],
          reason: "",
          commit_type: "",
          ev_data: null,
        },
  });

const ProformaForm = ({
  form,
  mode = "edit",
  edit,
  onSubmit,
  autoInvoice,
  data,
  loading: initialLoading,
  errors,
}: Props) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [formData, setFormData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [subTotal, setSubtotal] = useState(0);
  
  const calculateVatPercent = (autoInvoice: AutoInvoice) => {
    const {
      total_sub_amount = 0,
      additional_cost_discount = 0,
      vat_amount = 0,
      additional_type = 'ADDITIONAL CHARGE',
    } = autoInvoice;
  
    let vat_percent = 0;
    let total_amount = 0;
  
    if (vat_amount && vat_amount !== 0) {

      if (additional_type === 'ADDITIONAL CHARGE') {
        total_amount = (total_sub_amount + additional_cost_discount) +
          (total_sub_amount + additional_cost_discount) * (vat_amount / total_sub_amount);
      } else if (additional_type === 'ADDITIONAL DISCOUNT') {
        total_amount = (total_sub_amount - additional_cost_discount) +
          (total_sub_amount - additional_cost_discount) * (vat_amount / total_sub_amount);
      }

      vat_percent = (vat_amount / total_sub_amount) * 100;
    }
  
    return Math.round(vat_percent);
  };


  const [hiddenValues, setHiddenValues] = useState({
    invoice_type: "PROFORMA INVOICE",
    vat_percent: autoInvoice ? calculateVatPercent(autoInvoice) : 0,
    actual_total_amount: autoInvoice?.actual_total_amount || 0,
    exact_customer_id: autoInvoice?.exact_customer_id || "",
    client_country: autoInvoice?.client_country || "",
    reason: autoInvoice?.reason || "",
    commit_type: autoInvoice?.commit_type || "",
    total_sub_amount: autoInvoice?.total_sub_amount || 0,
    total_amount: autoInvoice?.total_amount || 0,
    vat_amount: autoInvoice?.vat_amount || 0,
    ev_data: null,
    calculated_data_html: autoInvoice?.calculated_data_html || "",
    additional_cost_discount: autoInvoice?.additional_cost_discount || 0,
    unchecked_columns: autoInvoice?.unchecked_columns || [],
  });

  const formatDateWithoutTime = (date: string) => {
    const formattedDate = new Date(date);
    return formattedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  };
  
  const handleSubmit = (data: any) => {
    let htmlContent = '';

    if (tableRef.current) {
      const clonedTable = tableRef.current.cloneNode(true) as HTMLTableElement;

      const inputElements = clonedTable.querySelectorAll('input, .react-number-format-input');
      inputElements.forEach((input) => {
        const value = (input as HTMLInputElement).value;
        const span = document.createElement('span');
        span.textContent = value;
        input.replaceWith(span);
      });
      htmlContent = clonedTable.outerHTML;
    }

    const payload = {
      auto_invoice: {
        ...data,
        calculated_data_html: htmlContent,
        ev_data: null,
        invoice_type: hiddenValues.invoice_type,
        vat_percent: hiddenValues.vat_percent,
        actual_total_amount: hiddenValues.actual_total_amount,
        client_country: hiddenValues.client_country,
        reason: hiddenValues.reason,
        commit_type: hiddenValues.commit_type,
        total_sub_amount: hiddenValues.total_sub_amount,
        total_amount: hiddenValues.total_amount,
        vat_amount: hiddenValues.vat_amount,
        additional_cost_discount: hiddenValues.additional_cost_discount,
        unchecked_columns: hiddenValues.unchecked_columns,
        exact_customer_id: hiddenValues.exact_customer_id,
        service_period_start: formatDateWithoutTime(data.service_period_start),
        service_period_end: formatDateWithoutTime(data.service_period_end),
      },
    };
    console.log(payload);
    onSubmit(payload as any);
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  useEffect(() => {
    if (!edit && autoInvoice)
      form.reset({
        ...autoInvoice,
        entity_id: autoInvoice?.entity?.id?.toString() || "",
        nmq_entity_id: autoInvoice?.nmq_entity?.id.toString() || "",
      });
  }, [edit, autoInvoice]);

  useEffect(() => {
    if (autoInvoice) {
      form.reset({
        ...autoInvoice,
        entity_id: autoInvoice?.entity?.id?.toString() || "",
        nmq_entity_id: autoInvoice?.nmq_entity?.id.toString() || "",
      });
    }
  }, [autoInvoice]);

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);
  

  useEffect(() => {
    const subTotalWithAdditional = parseFloat(
      (
        subTotal +
        (form.getValues('additional_type') === 'ADDITIONAL CHARGE'
          ? +hiddenValues.additional_cost_discount
          : -hiddenValues.additional_cost_discount)
      ).toFixed(2)
    );
  
    const vatAmount = parseFloat(
      ((subTotalWithAdditional * hiddenValues.vat_percent) / 100).toFixed(2)
    );
  
    const totalAmount = parseFloat(
      (subTotalWithAdditional + vatAmount).toFixed(2)
    );

    const actualTotalAmount = parseFloat((subTotal + ((subTotal * hiddenValues.vat_percent) / 100)).toFixed(2));

    setHiddenValues((prevValues) => ({
      ...prevValues,
      total_sub_amount: subTotalWithAdditional,
      vat_amount: vatAmount,
      total_amount: totalAmount,
      actual_total_amount: actualTotalAmount
    }));
  }, [subTotal]);

  async function handleAccountChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/accounts/${value}/read_account`);

    setData({
      ...data,
      entities: response.data.entities,
    });
  }

  async function handleEntityChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/entities/${value}/read_entity`);

    setData({
      ...data,
      nmq_entities: response.data.nmq_entities
    });

    fetchExactData({ setData: setFormData });
  }

  async function handleNMQEntityChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    fetchExactData({ setData: setFormData });
  }

  async function fetchExactData({ setData } : { setData: React.Dispatch<React.SetStateAction<ProformaProps>> }) {
    const accountId = form.getValues('account_id');
    const entityId = form.getValues('entity_id');
    const nmqEntityId = form.getValues('nmq_entity_id');

    if (accountId && entityId && nmqEntityId) {
      setLoading(true);

      try {
        const csrfToken = getCsrfToken();
        const response = await axios.post(`/auto_invoices/get_exact_data`, {
          account: accountId,
          entity: entityId,
          nmq_entity: nmqEntityId
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        const {
          VAT,
          clientsAddress,
          clientsVat,
          currency,
          inVoiceQuery,
          paymentTerms,
          clientCountry,
          customerAccountId,
          client_contact,
        } = response.data;

        setData((prevData) => ({
          ...prevData,
          client_address: clientsAddress,
          client_contact,
          client_vat: clientsVat,
          currency_type: currency,
          invoicing_queries: inVoiceQuery,
          payment_terms: paymentTerms,
        }));

        form.setValue('client_address', clientsAddress);
        form.setValue('client_contact', client_contact);
        form.setValue('client_vat', clientsVat);
        form.setValue('currency_type', currency);
        form.setValue('invoicing_queries', inVoiceQuery);
        form.setValue('payment_terms', paymentTerms);


        setHiddenValues((prevValues) => ({
          ...prevValues,
          exact_customer_id: customerAccountId,
          client_country: clientCountry,
          vat_percent: VAT,
        }));
        
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }

      
    }
  }

  const handleColumnsChange = (unVisibleColumns: any[]) => {
    setHiddenValues((prevValues) => ({
      ...prevValues,
      unchecked_columns: unVisibleColumns.map(column => column.value),
    }));
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-8 w-full"
      >
        
        {loading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <Spinner />
          </div>
        )}

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <DatePicker
                    placeholder="Date"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                    }}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="account_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Account*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.accounts.map((account) => ({
                      label: account.name,
                      value: account.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) => handleAccountChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                    enabledSearch={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="entity_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-2/6">
                <FormLabel>Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.entities.map((entity) => ({
                      label: entity.name,
                      value: entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) =>  handleEntityChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nmq_entity_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>NMQ Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.nmq_entities.map((nmq_entity) => ({
                      label: nmq_entity.name,
                      value: nmq_entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) =>  handleNMQEntityChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="service_period_start"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Service period Start</FormLabel>
                <FormControl>
                  <DatePicker
                    placeholder="Service period Start"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                    }}
                    mode="month"
                    monthSelectionType="start"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service_period_end"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Service Period End</FormLabel>
                <FormControl>
                  <DatePicker 
                    placeholder="Service Period End"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                    }}
                    mode="month"
                    monthSelectionType="end"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="reference_no"
            render={({ field }) => (
              <FormItem className="w-3/6">
                <FormLabel>Reference No</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Reference No"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="order_ref_no"
            render={({ field }) => (
              <FormItem className="w-2/6">
                <FormLabel>Order/PO Number</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Order/PO Number"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="payment_terms"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Payment Terms</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Payment Terms"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="invoicing_queries"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Invoicing Queries</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Invoicing Queries"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency_type"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Select
                    options={Constants.CURRENCIES.map((currency) => ({
                      label: currency,
                      value: currency,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="client_contact"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client Contact</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Client Contact"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="client_address"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client Address</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Client Address"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="client_vat"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client VAT</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Client VAT"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="w-3/6">
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Notes"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="service_name"
            render={({ field }) => (
              <FormItem className="w-2/6">
                <FormLabel>Service Name</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Service Name"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-center px-12">
          {!loading && hiddenValues.exact_customer_id && (
            <ManualInvoiceTable
              ref={tableRef}
              currency={form.getValues('currency_type')}
              setSubtotal={setSubtotal}
              unVisibleColumns={hiddenValues.unchecked_columns}
              onColumnsChange={handleColumnsChange}
              isCreditNote={false}
            />
          )}
        </div>
        
        <div className="flex justify-center px-12 !my-0">
        {!loading && hiddenValues.exact_customer_id && (
          <div className="w-full flex items-center justify-between ">
            <FormField
              control={form.control}
              name="additional_type"
              render={({ field }) => (
                <FormItem className="flex flex-col w-1/4">
                  <FormLabel></FormLabel>
                  <FormControl>
                    <Select
                      options={[
                        { label: "ADDITIONAL CHARGE", value: "ADDITIONAL CHARGE" },
                        { label: "ADDITIONAL DISCOUNT", value: "ADDITIONAL DISCOUNT" },
                      ]}
                      value={field.value || "ADDITIONAL CHARGE"}
                      onChange={(value) => {
                        field.onChange(value);
                        setHiddenValues((prevValues) => ({
                          ...prevValues,
                          total_sub_amount:
                            subTotal +
                            (value === 'ADDITIONAL CHARGE'
                              ? +parseFloat(value)
                              : -parseFloat(value)),
                          additional_cost_discount: parseFloat(value),
                        }));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="additional_amount_description"
              render={({ field }) => (
                <FormItem className="flex flex-col w-1/4 ml-4">
                  <FormLabel></FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      disabled={!edit}
                      placeholder="Enter description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="additional_cost_discount"
              render={({ field }) => (
                <FormItem className="flex flex-col w-44 ml-4">
                  <FormLabel className="text-right"></FormLabel>
                  <FormControl className="">
                    <Controller
                      name="additional_cost_discount"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <ForwardedNumericFormat
                          {...controllerField}
                          value={controllerField.value || ""}
                          onValueChange={(values) => {
                            const { value } = values;
                            controllerField.onChange(value);
                            setHiddenValues((prevValues) => ({
                              ...prevValues,
                              total_sub_amount:
                                subTotal +
                                (form.getValues('additional_type') === 'ADDITIONAL DISCOUNT'
                                  ? -parseFloat(value)
                                  : parseFloat(value)),
                              additional_cost_discount: parseFloat(value),
                            }));
                          }}
                          thousandSeparator=","
                          decimalSeparator="."
                          fixedDecimalScale
                          decimalScale={2}
                          placeholder="0.00"
                          disabled={!edit}
                          className="border p-1 w-full text-right"
                          prefix={`${form.getValues('currency_type')} `}
                        />
                      )}
                    />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        )}
        </div>

        <div className="flex px-12">
          {!loading && hiddenValues.exact_customer_id && (
            <FormField
              control={form.control}
              name="total_sub_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">SubTotal Amount</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="total_sub_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.total_sub_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                              setHiddenValues((prevValues) => ({
                                ...prevValues,
                                vat_amount: (parseFloat(value) * hiddenValues.vat_percent) / 100,
                                total_amount: parseFloat(value) + (parseFloat(value) * hiddenValues.vat_percent) / 100,
                              }));
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={true}
                            className="border p-1 w-full text-right"
                            prefix={`${form.getValues('currency_type')} `}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>


        <div className="flex justify-center px-12">
          {!loading && hiddenValues.exact_customer_id && (
            <FormField
              control={form.control}
              name="vat_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">{`VAT %${hiddenValues.vat_percent}`}</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="vat_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.vat_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                              setHiddenValues((prevValues) => ({
                                ...prevValues,
                                vat_amount: parseFloat(value),
                                total_amount: hiddenValues.total_sub_amount + parseFloat(value),
                              }));
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={!edit}
                            className="border p-1 w-full text-right"
                            prefix={`${form.getValues('currency_type')} `}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="flex justify-center px-12">
          {!loading && hiddenValues.exact_customer_id && (
            <FormField
              control={form.control}
              name="total_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">Total Amount</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="total_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.total_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={true}
                            className="border p-1 w-full text-right"
                            prefix={`${form.getValues('currency_type')} `}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <div className="flex gap-2 !mb-4">
          {mode === "create" && (
            <Link href="/auto_invoices">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit || loading}>
            {loading ? 'Loading...' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ProformaForm;
